import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, Copy, Check, Sparkles, User } from "lucide-react";
import { useToastContext } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface SuggestedPrompt {
  id: string;
  text: string;
}

interface ChatComponentProps {
  onSendMessage: (message: string) => Promise<void>;
  suggestedPrompts?: SuggestedPrompt[];
  className?: string;
  initialMessage?: string;
  messages?: Message[];
  disabled?: boolean;
  scrollOnMount?: boolean;
  isGenerating?: boolean;
}

const ChatComponent: React.FC<ChatComponentProps> = ({
  onSendMessage,
  suggestedPrompts = [],
  className = "",
  initialMessage,
  messages = [],
  disabled = false,
  scrollOnMount = false,
  isGenerating = false,
}) => {
  const { showToast } = useToastContext();
  const { userProfile } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;

    const newMessage: Message = {
      role: "user",
      content: inputValue.trim(),
    };

    setInputValue("");
    setIsSending(true);

    try {
      await onSendMessage(inputValue.trim());
      scrollToBottom();
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleCopy = async (text: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageIndex);
      showToast("success", "Message copied to clipboard");

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      showToast("error", "Failed to copy message");
    }
  };

  return (
    <div className={`flex flex-col h-full bg-background-default dark:bg-background-dark ${className}`}>
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto min-h-0 scrollbar-hide"
      >
        <div className="px-4 py-8">
          <div className="max-w-3xl mx-auto space-y-8">
            {initialMessage && messages.length === 0 && (
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg bg-background-panel dark:bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-secondary" />
                </div>
                <div className="flex-1 pt-1">
                  <p className="text-sm text-text-primary dark:text-text-primaryDark whitespace-pre-wrap break-words leading-relaxed">
                    {initialMessage}
                  </p>
                </div>
              </div>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex items-start gap-4 ${
                  message.role === "assistant" ? "justify-start" : "justify-end"
                }`}
              >
                {message.role === "assistant" && (
                  <div className="w-8 h-8 rounded-lg bg-background-panel dark:bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-secondary" />
                  </div>
                )}

                <div className={`flex-1 ${message.role === "user" ? "flex justify-end" : ""}`}>
                  <div className={`max-w-[80%] ${message.role === "user" ? "flex flex-col items-end" : ""}`}>
                    <div
                      className={`
                        relative group/message
                        rounded-lg px-4 py-3
                        ${
                          message.role === "assistant"
                            ? "bg-background-paper dark:bg-background-paperDark"
                            : "bg-secondary text-white"
                        }
                      `}
                    >
                      {message.role === "assistant" && (
                        <button
                          onClick={() => handleCopy(message.content, index)}
                          className={`
                            absolute -top-1 -right-1
                            p-1.5 rounded-md
                            opacity-0 group-hover/message:opacity-100
                            bg-background-paper dark:bg-background-paperDark
                            border border-divider dark:border-divider-dark
                            transition-all duration-200
                            hover:bg-background-panel dark:hover:bg-background-panelDark
                            ${
                              copiedMessageId === index
                                ? "text-secondary opacity-100"
                                : "text-text-secondary hover:text-text-primary dark:text-text-secondaryDark dark:hover:text-text-primaryDark"
                            }
                          `}
                          title="Copy message"
                        >
                          {copiedMessageId === index ? (
                            <Check className="w-3.5 h-3.5" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                      <p
                        className={`
                          text-sm whitespace-pre-wrap break-words leading-relaxed
                          ${message.role === "user" ? "text-white" : "text-text-primary dark:text-text-primaryDark"}
                        `}
                      >
                        {message.content}
                      </p>
                    </div>
                  </div>
                </div>

                {message.role === "user" && (
                  <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {userProfile?.avatar ? (
                      <img
                        src={userProfile.avatar}
                        alt="User"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                )}
              </div>
            ))}
            {isGenerating && (
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg bg-background-panel dark:bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-secondary" />
                </div>
                <div className="flex-1 pt-1">
                  <div className="bg-background-paper dark:bg-background-paperDark rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-text-secondary dark:bg-text-secondaryDark rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-1.5 h-1.5 bg-text-secondary dark:bg-text-secondaryDark rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-1.5 h-1.5 bg-text-secondary dark:bg-text-secondaryDark rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} style={{ height: 1 }} />
          </div>
        </div>
      </div>

      {suggestedPrompts.length > 0 && messages.length === 0 && (
        <div className="flex-shrink-0 border-t border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark">
          <div className="px-4 py-4">
            <div className="max-w-3xl mx-auto">
              <p className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-3 px-1">Suggested questions</p>
              <div className="flex flex-wrap gap-2">
                {suggestedPrompts.map((prompt) => (
                  <button
                    key={prompt.id}
                    onClick={() => handlePromptClick(prompt.text)}
                    disabled={isGenerating || disabled}
                    className={`
                      flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium
                      transition-all duration-200
                      ${
                        isGenerating || disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-background-panel dark:hover:bg-background-panelDark active:scale-[0.98]"
                      }
                      bg-background-paper dark:bg-background-paperDark 
                      text-text-primary dark:text-text-primaryDark 
                      border border-divider dark:border-divider-dark
                    `}
                  >
                    {prompt.text}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-shrink-0 border-t border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark">
        <div className="px-4 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="relative flex items-end gap-2 bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark p-3">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isGenerating || disabled}
                className={`
                  flex-1 py-2 px-0
                  bg-transparent
                  text-sm
                  text-text-primary dark:text-text-primaryDark
                  placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark
                  resize-none outline-none
                  min-h-[44px] max-h-32
                  ${
                    isGenerating || disabled
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }
                `}
                placeholder={
                  isGenerating
                    ? "Generating response..."
                    : disabled
                    ? "Chat disabled"
                    : "Ask a question about your data..."
                }
              />
              <button
                onClick={handleSend}
                disabled={isGenerating || disabled || !inputValue.trim()}
                className={`
                  flex-shrink-0
                  p-2.5 rounded-lg
                  transition-all duration-200
                  ${
                    inputValue.trim() && !disabled && !isGenerating
                      ? "bg-secondary text-white hover:bg-secondary-hover active:scale-95"
                      : "bg-background-panel dark:bg-background-panelDark text-text-secondary dark:text-text-secondaryDark"
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatComponent;
